import React from "react";

interface SummaryCardsRowProps {
  totalScheduled: number;
  // Scheduled employee-days that have already elapsed. Equals totalScheduled
  // for live/past views; smaller when the range extends into the future, in
  // which case the percentage subtitles compare against it instead — a week
  // viewed on Wednesday must not read 35% attendance just because
  // Thursday-Sunday haven't happened yet.
  totalScheduledToDate?: number;
  totalClockedIn: number;
  totalNoShows: number;
  totalLateArrivals: number;
  regionsActive: number;
  totalRegions: number;
  totalSites: number;
}

interface CardProps {
  label: string;
  value: string | number;
  subtitle: string;
}

const SummaryCard: React.FC<CardProps> = ({ label, value, subtitle }) => (
  <div
    style={{
      flex: 1,
      backgroundColor: "#fff",
      border: "1px solid #cecece",
      borderRadius: 8,
      padding: "17px 25px",
      minWidth: 0,
    }}
  >
    <p
      style={{
        fontSize: 12,
        fontWeight: 500,
        color: "#6a7a85",
        margin: 0,
        lineHeight: "18px",
      }}
    >
      {label}
    </p>
    <p
      style={{
        fontSize: 24,
        fontWeight: 600,
        color: "#000",
        margin: "4px 0",
        lineHeight: "36px",
      }}
    >
      {typeof value === "number" ? value.toLocaleString() : value}
    </p>
    <p
      style={{
        fontSize: 12,
        fontWeight: 400,
        color: "#6a7a85",
        margin: 0,
        lineHeight: "18px",
      }}
    >
      {subtitle}
    </p>
  </div>
);

const SummaryCardsRow: React.FC<SummaryCardsRowProps> = ({
  totalScheduled,
  totalScheduledToDate,
  totalClockedIn,
  totalNoShows,
  totalLateArrivals,
  regionsActive,
  totalRegions,
  totalSites,
}) => {
  const elapsedScheduled = totalScheduledToDate ?? totalScheduled;
  const rangeInProgress = elapsedScheduled < totalScheduled;
  const percentOf = (count: number): string =>
    elapsedScheduled > 0 ? ((count / elapsedScheduled) * 100).toFixed(1) : "0";

  let attendanceSubtitle = `${percentOf(totalClockedIn)}% attendance`;
  if (rangeInProgress) {
    attendanceSubtitle =
      elapsedScheduled > 0
        ? `${percentOf(
            totalClockedIn
          )}% of ${elapsedScheduled.toLocaleString()} scheduled so far`
        : "range hasn't started";
  }
  const ofScheduled = rangeInProgress
    ? "% of scheduled so far"
    : "% of scheduled";

  let regionsSubtitle = `${regionsActive} regions`;
  if (totalRegions === 0) regionsSubtitle = "no groups yet";
  else if (regionsActive === totalRegions) regionsSubtitle = "all regions";

  return (
    <div className="grid grid-cols-2 gap-sm mb-lg sm:flex sm:gap-md sm:mb-lg">
      {/*
        Phone: 2-up grid (5th card sits alone on its own row).
        Desktop: horizontal flex row with each SummaryCard's flex:1 sharing the space.
      */}
      <SummaryCard
        label="Scheduled"
        value={totalScheduled}
        subtitle={`Across ${totalSites} sites`}
      />
      <SummaryCard
        label="Clocked in"
        value={totalClockedIn}
        subtitle={attendanceSubtitle}
      />
      <SummaryCard
        label="No Shows"
        value={totalNoShows}
        subtitle={`${percentOf(totalNoShows)}${ofScheduled}`}
      />
      <SummaryCard
        label="Late Arrivals"
        value={totalLateArrivals}
        subtitle={`${percentOf(totalLateArrivals)}${ofScheduled}`}
      />
      <SummaryCard
        label="Regions Active"
        value={`${regionsActive}/${totalRegions}`}
        subtitle={regionsSubtitle}
      />
    </div>
  );
};

export default SummaryCardsRow;
